import {Color, Icon} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import FormGroup from 'ui-library/bs/FormGroup';
import TextArea from 'ui-library/bs/TextArea';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

export interface GenerateStudyReportResult {
  prompt: string;
  file: File | null;
}

export interface GenerateStudyReportModalProps {
  studyName: string;
  studyApiId: string;
  defaultPrompt?: string;
  defaultFile?: File | null;
  onRequestSubmit: (result: GenerateStudyReportResult) => void;
  onRequestClose: () => void;
}

// Used in a regex to strip from prompt text — avoid adding regex-special characters.
const TEMPLATE_INSTRUCTION = 'Follow the provided report template file.';

function getDefaultPrompt(studyName: string, studyApiId: string): string {
  return `Please write a report about Study ${studyName} (${studyApiId})

Describe the type of report you're drafting (e.g., research summary reports, CMC toxicology reports, qualification and process development reports):



Additional context:

`;
}

export default function GenerateStudyReportModal(props: GenerateStudyReportModalProps): JSX.Element {
  const [prompt, setPrompt] = useState(
    () => props.defaultPrompt ?? getDefaultPrompt(props.studyName, props.studyApiId)
  );
  const [file, setFile] = useState<File | null>(props.defaultFile ?? null);

  return (
    <Modal
      title={
        <>
          <Icon icon="sparkle" className={css(styles.sparkleIcon)} /> Generate a report with Deep Research
        </>
      }
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit({prompt: prompt.trim(), file});
      }}
      buttons={[
        {
          text: 'Generate',
          submit: true,
          disabled: !prompt.trim(),
        },
      ]}
    >
      <p>
        Use Benchling&apos;s{' '}
        <a
          href="https://help.benchling.com/hc/en-us/articles/38679507502733-Search-analyze-and-answer-complex-questions-about-your-data-with-the-Deep-Research-agent"
          target="_blank"
          rel="noopener noreferrer"
        >
          Deep Research Agent
        </a>{' '}
        to draft a report related to this study.
      </p>
      <FormGroup>
        <FieldLabel label="Report template" />
        <p>Attach a report template to customize your report structure, if desired.</p>
        <FileChooser
          multiple={false}
          accept=".docx,.pdf,.doc,.txt"
          onSelectFiles={(selectedFile: File) => {
            setFile(selectedFile);
            setPrompt((prev) =>
              prev.includes(TEMPLATE_INSTRUCTION) ? prev : `${prev.trimEnd()}\n\n${TEMPLATE_INSTRUCTION}`
            );
          }}
        />
        {file && (
          <FileList>
            <FileRow
              file={file}
              onRequestRemove={() => {
                setFile(null);
                setPrompt((prev) =>
                  prev.replace(
                    new RegExp(`\\n*${TEMPLATE_INSTRUCTION.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
                    ''
                  )
                );
              }}
            />
          </FileList>
        )}
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Prompt" isRequired={true} />
        <p>Edit or add instructions below.</p>
        <TextArea
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
          rows={10}
        />
      </FormGroup>
    </Modal>
  );
}

const styles = StyleSheet.create({
  sparkleIcon: {
    color: Color.BRAND_ROYAL,
    paddingRight: 8,
  },
});
